import { useMutation, useQueryClient } from '@tanstack/react-query';

import { customerApi } from '../services/api'; // createCustomerBasic direk import edilmeyecek
import { saveCustomerProperties } from '../services/customerPropertiesApi';
import { DEFAULT_CUSTOMER_PROPERTIES } from '../services/customerPropertiesDefaults';

export interface CustomerResponse {
  customerCode: string;
  customerName: string;
  success?: boolean;
  message?: string;
  errorDetails?: string;
  // Diğer müşteri yanıt alanları
}

export interface CustomerCreateRequest {
  customerCode: string;
  customerName: string;
  customerSurname?: string;
  customerTitle?: string;
  taxNumber?: string;
  customerIdentityNumber?: string;
  identityNum?: string; // TC Kimlik numarası
  customerTypeCode: number;
  isRealPerson?: boolean; // Gerçek kişi mi?
  discountGroupCode?: string;
  paymentPlanGroupCode?: string;
  currencyCode?: string;
  exchangeTypeCode?: string; // Para birimi kodu (frontend'de bu isimle geliyor)
  officeCode: string;
  salesmanCode?: string;
  creditLimit: number;
  riskLimit?: number;
  isSubjectToEInvoice?: boolean; // E-fatura tabi mi?
  isSubjectToEDispatch?: boolean; // E-irsaliye tabi mi?
  isSubjectToEShipment?: boolean; // E-irsaliye tabi mi? (frontend'de bu isimle geliyor)
  eInvoiceStartDate?: Date | null; // E-fatura başlangıç tarihi
  eShipmentStartDate?: Date | null; // E-irsaliye başlangıç tarihi
  contacts?: {
    contactTypeCode: string;
    contact: string;
    isDefault: boolean;
  }[];
  addresses?: {
    addressTypeCode: string;
    address: string;
    countryCode: string;
    stateCode?: string;
    cityCode: string;
    districtCode: string;
    postalCode?: string;
    isDefault: boolean;
    isBlocked: boolean;
  }[];
  taxOffice?: string;
  regionCode?: string;
  cityCode?: string;
  districtCode?: string;
  isBlocked: boolean;
  communications?: {
    communicationTypeCode: string;
    communication: string;
    isDefault: boolean;
  }[];
  // Ek alanlar
  stateCode?: string;
  address?: string;
  contactName?: string;
}

export interface UseCustomerCreateResult {
  mutate: (customerData: CustomerCreateRequest, options?: {
    onSuccess?: (response: CustomerResponse) => void;
    onError?: (error: Error) => void;
  }) => void;
  mutateAsync: (customerData: CustomerCreateRequest) => Promise<CustomerResponse>;
  isPending: boolean;
  error: Error | null;
}

const useCustomerCreate = (): UseCustomerCreateResult => {
  const queryClient = useQueryClient();

  const mutation = useMutation<CustomerResponse, Error, CustomerCreateRequest>({
    mutationFn: async (customerData) => {
      // Müşteri verilerini backend'in beklediği formata dönüştür
      // TypeScript hatalarını önlemek için Record<string, any> tipini kullanıyoruz
      const formattedData: Record<string, any> = {
        // Zorunlu alanlar
        CustomerCode: customerData.customerCode || '',
        CustomerName: customerData.customerName || '',
        CustomerTypeCode: Number(customerData.customerTypeCode) || 0, // byte tipinde
        CompanyCode: 1, // short tipinde
        OfficeCode: customerData.officeCode || 'M', // string tipinde, varsayılan 'M'
        CountryCode: 'TR', // string tipinde, varsayılan 'TR'
        StateCode: customerData.stateCode || '',
        CityCode: customerData.cityCode || '',
        DistrictCode: customerData.districtCode || '',
        Address: customerData.address || '',
        ContactName: customerData.contactName || '',
        
        // Diğer alanlar (opsiyonel)
        CustomerSurname: customerData.customerSurname || '',
        TaxNumber: customerData.taxNumber || '',
        TaxOfficeCode: customerData.taxOffice || '', // Vergi dairesi kodu - frontend'de taxOffice olarak geliyor
        IdentityNum: customerData.identityNum || customerData.customerIdentityNumber || '',
        // Para birimi
        CurrencyCode: customerData.currencyCode || customerData.exchangeTypeCode || 'USD',
        // Gerçek kişi/tüzel kişi ayrımı
        IsIndividualAcc: customerData.isRealPerson === true, // Gerçek kişi ise true, tüzel kişi ise false
        // E-Fatura ve E-İrsaliye bilgileri
        IsSubjectToEInvoice: customerData.isSubjectToEInvoice || false,
        IsSubjectToEDispatch: customerData.isSubjectToEShipment || customerData.isSubjectToEDispatch || false,
        // Sistem alanları
        CreatedUserName: 'system',
        LastUpdatedUserName: 'system',
        // Tarih alanları için boş alanlar
        EInvoiceStartDate: null,
        EShipmentStartDate: null
      };

      // E-Fatura başlangıç tarihi
      if (customerData.isSubjectToEInvoice && customerData.eInvoiceStartDate) {
        formattedData.EInvoiceStartDate = customerData.eInvoiceStartDate;
      }

      // E-İrsaliye başlangıç tarihi
      if (customerData.isSubjectToEShipment && customerData.eShipmentStartDate) {
        formattedData.EShipmentStartDate = customerData.eShipmentStartDate;
      }

      // Detaylar: Addresses
      // Eğer müşteriData.addresses varsa doğrudan map et; yoksa tekil alanlardan bir varsayılan adres üret
      const addresses: any[] = [];
      if (Array.isArray((customerData as any).addresses) && (customerData as any).addresses.length > 0) {
        for (const a of (customerData as any).addresses) {
          addresses.push({
            CustomerCode: formattedData.CustomerCode,
            AddressTypeCode: a.addressTypeCode,
            Address: a.address,
            CountryCode: a.countryCode || formattedData.CountryCode,
            StateCode: a.stateCode || formattedData.StateCode,
            CityCode: a.cityCode || formattedData.CityCode,
            DistrictCode: a.districtCode || formattedData.DistrictCode,
            TaxOfficeCode: formattedData.TaxOfficeCode || undefined,
            TaxNumber: formattedData.TaxNumber || undefined,
            IsBlocked: a.isBlocked ?? false,
            IsDefault: a.isDefault ?? true,
            CreatedUserName: formattedData.CreatedUserName,
            LastUpdatedUserName: formattedData.LastUpdatedUserName,
          });
        }
      } else {
        // Tekil alanlardan (quick form) bir adet varsayılan fatura adresi üret
        if (formattedData.Address && (formattedData.CityCode || formattedData.DistrictCode)) {
          addresses.push({
            CustomerCode: formattedData.CustomerCode,
            AddressTypeCode: 'INV', // varsayılan: fatura adresi
            Address: formattedData.Address,
            CountryCode: formattedData.CountryCode,
            StateCode: formattedData.StateCode,
            CityCode: formattedData.CityCode,
            DistrictCode: formattedData.DistrictCode,
            TaxOfficeCode: formattedData.TaxOfficeCode || undefined,
            TaxNumber: formattedData.TaxNumber || undefined,
            IsBlocked: false,
            IsDefault: true,
            CreatedUserName: formattedData.CreatedUserName,
            LastUpdatedUserName: formattedData.LastUpdatedUserName,
          });
        }
      }
      if (addresses.length > 0) {
        formattedData.Addresses = addresses;
      }

      // Detaylar: Communications
      const communications: any[] = [];
      const anyCustomer: any = customerData as any;
      if (Array.isArray(anyCustomer.communications) && anyCustomer.communications.length > 0) {
        for (const c of anyCustomer.communications) {
          communications.push({
            CustomerCode: formattedData.CustomerCode,
            CommunicationTypeCode: c.communicationTypeCode, // ör: 'TEL', 'MAIL'
            CommAddress: c.communication,
            CanSendAdvert: false,
            IsBlocked: false,
            IsDefault: c.isDefault ?? false,
            CreatedUserName: formattedData.CreatedUserName,
            LastUpdatedUserName: formattedData.LastUpdatedUserName,
          });
        }
      } else {
        // Quick form alanları: authPhone, accountingPhone, authEmail, accountingEmail
        const addComm = (type: string, value?: string | null, isDefault?: boolean) => {
          if (value && String(value).trim().length > 0) {
            communications.push({
              CustomerCode: formattedData.CustomerCode,
              CommunicationTypeCode: type,
              CommAddress: value,
              CanSendAdvert: false,
              IsBlocked: false,
              IsDefault: !!isDefault,
              CreatedUserName: formattedData.CreatedUserName,
              LastUpdatedUserName: formattedData.LastUpdatedUserName,
            });
          }
        };
        addComm('TEL', (anyCustomer as any).authPhone, true);
        addComm('TEL', (anyCustomer as any).accountingPhone, false);
        addComm('MAIL', (anyCustomer as any).authEmail, false);
        addComm('MAIL', (anyCustomer as any).accountingEmail, false);
      }
      if (communications.length > 0) {
        formattedData.Communications = communications;
      }

      console.log('Müşteri oluşturma için hazırlanan veriler:', formattedData);

      // API'ye istek gönder
      const response = await customerApi.createCustomerBasic(formattedData);

      // API yanıtını CustomerResponse tipine dönüştür
      const customerResponse: CustomerResponse = {
        customerCode: formattedData.CustomerCode,
        customerName: formattedData.CustomerName,
        // Diğer gerekli alanları ekle
        ...response.data
      };
      
      return customerResponse;
    },
    onSuccess: async (response) => {
      console.log('Müşteri başarıyla oluşturuldu:', response);
      // Müşteri listesi sorgusunu invalidate et
      queryClient.invalidateQueries({ queryKey: ['customers'] });

      // Varsayılan müşteri özelliklerini otomatik kaydet (frontend-only)
      try {
        const code = response?.customerCode;
        // Default sözlük boş değilse ve müşteri kodu varsa
        const hasDefaults = DEFAULT_CUSTOMER_PROPERTIES && Object.keys(DEFAULT_CUSTOMER_PROPERTIES).length > 0;
        if (code && hasDefaults) {
          console.log('Varsayılan müşteri özellikleri kaydediliyor...', DEFAULT_CUSTOMER_PROPERTIES);
          await saveCustomerProperties(code, DEFAULT_CUSTOMER_PROPERTIES as Record<number, string>);
          console.log('Varsayılan müşteri özellikleri kaydedildi.');
        } else {
          console.log('Varsayılan müşteri özellikleri boş veya müşteri kodu yok, atlanıyor.');
        }
      } catch (e) {
        console.warn('Varsayılan müşteri özellikleri kaydedilirken hata (akışı bozma):', e);
      }
    },
    onError: (error) => {
      console.error('Müşteri oluşturma hatası:', error);
    }
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error
  };
};

export default useCustomerCreate;